import React, { useState, useRef, useEffect } from "react";
import {
  MapPin,
  Calendar,
  Users,
  Search,
  Zap,
  Tag,
  Gift,
  Headphones,
  Plus,
  Minus,
  Star,
  Smartphone,
} from "lucide-react";
import "./CSS/Hotel.css";
import heroBg from "./Images/hotelbg.png";
import sliderimage1 from "./Images/niraj-golhar-7sASTzCacdA-unsplash.jpg";
import sliderimage2 from "./Images/sara-dubler-Koei_7yYtIo-unsplash.jpg";
import sliderimage3 from "./Images/valeriia-bugaiova-_pPHgeHz1uk-unsplash.jpg";
import offerImg1 from "./Images/flight-offer-1.jpg";
import offerImg2 from "./Images/flight-offer-2.jpg";
import offerImg3 from "./Images/flight-offer-3.jpg";
import CarLoader from "../Hotel/CarLoader";

// TODO: add more hero photos to Images/ for a richer slider — reusing
// available images for now so the slider works out of the box
const HERO_SLIDES = [heroBg, sliderimage1, sliderimage2, sliderimage3];

const SLIDE_INTERVAL_MS = 5000;

// TODO: replace with real city-search API integration
const DEMO_CITY = {
  name: "PUNE",
  subtitle: "CITY IN, INDIA",
  country: "INDIA",
};

// Search results / navigation
const HOTEL_SEARCH_API = "https://api.hotel.namantechnolab.com/api/hotel/search";
const HOTEL_ENGINE_ID = 2;
const DEFAULT_NATIONALITY_CODE = "106";
const DEFAULT_COUNTRY_RES = "GB";
const DEFAULT_CURRENCY = "GBP";

// City / Country autosuggest API
const CITY_SEARCH_API =
  "https://api.hotel.namantechnolab.com/api/Static/CountryCitySearch?CityName=";
const CITY_SEARCH_MIN_CHARS = 3;
const CITY_SEARCH_DEBOUNCE_MS = 300;

// TODO: replace with real offers API integration
const DEMO_OFFERS = [
  { id: 1, image: offerImg1, alt: "Travel the world" },
  { id: 2, image: offerImg2, alt: "Explore the world" },
  { id: 3, image: offerImg3, alt: "Choose your destination" },
];

const WHY_BOOK_WITH_US = [
  {
    id: "quick",
    icon: Zap,
    title: "Quick Booking",
    text: "We provide quick and convenient flight bookings with amazing discounts.",
  },
  {
    id: "price",
    icon: Tag,
    title: "Lowest Price",
    text: "We ensure lowest pricing on hotel reservations and airline tickets.",
  },
  {
    id: "offers",
    icon: Gift,
    title: "Exciting Offers",
    text: "Enjoy exciting offers on flights, hotels and buses booking.",
  },
  {
    id: "support",
    icon: Headphones,
    title: "24/7 Support",
    text: "Get 24/7 support for booking related queries. We're here to help.",
  },
];

const STAR_OPTIONS = [1, 2, 3, 4, 5];

function formatDateParts(date) {
  const day = date.toLocaleDateString("en-GB", { day: "2-digit" });
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const year = date.toLocaleDateString("en-GB", { year: "numeric" });
  const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });
  return { day, month, year, weekday };
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function nightsBetween(checkIn, checkOut) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.round((startOfDay(checkOut) - startOfDay(checkIn)) / msPerDay));
}

function RoomsGuestsPanel({ rooms, setRooms, onDone }) {
  const updateAdult = (roomIndex, delta) => {
    setRooms((prev) =>
      prev.map((r, i) =>
        i === roomIndex
          ? { ...r, adults: Math.max(1, Math.min(9, r.adults + delta)) }
          : r
      )
    );
  };

  const updateChild = (roomIndex, delta) => {
    setRooms((prev) =>
      prev.map((r, i) =>
        i === roomIndex
          ? { ...r, children: Math.max(0, Math.min(9, r.children + delta)) }
          : r
      )
    );
  };
  const addRoom = () => {
    if (rooms.length >= 5) return;
    setRooms((prev) => [...prev, { adults: 1, children: 0 }]);
  };

  const removeRoom = () => {
    if (rooms.length <= 1) return;
    setRooms((prev) => prev.slice(0, -1));
  };

  return (
    <div className="hotel-search__pax-panel">
      {rooms.map((room, index) => (
        <div className="hotel-search__pax-room" key={index}>
          <h6 className="hotel-search__pax-room-title">Room {index + 1}</h6>
          <div className="hotel-search__pax-row">
            <div className="hotel-search__pax-counter">
              <span className="hotel-search__pax-counter-label">Adult(s)</span>
              <div className="hotel-search__pax-counter-controls">
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateAdult(index, -1)}
                  aria-label="Decrease adults"
                >
                  <Minus size={14} />
                </button>
                <span className="hotel-search__pax-value">{room.adults}</span>
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateAdult(index, 1)}
                  aria-label="Increase adults"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            <div className="hotel-search__pax-counter">
              <span className="hotel-search__pax-counter-label">Child(ren)</span>
              <div className="hotel-search__pax-counter-controls">
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateChild(index, -1)}
                  aria-label="Decrease children"
                >
                  <Minus size={14} />
                </button>
                <span className="hotel-search__pax-value">{room.children}</span>
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateChild(index, 1)}
                  aria-label="Increase children"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="hotel-search__pax-actions">
        <button
          type="button"
          className="hotel-search__pax-add"
          onClick={addRoom}
          disabled={rooms.length >= 5}
        >
          Add Room
        </button>
        {rooms.length > 1 && (
          <button
            type="button"
            className="hotel-search__pax-remove"
            onClick={removeRoom}
          >
            Remove
          </button>
        )}
        <button type="button" className="hotel-search__pax-done" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function CityAutosuggest({ city, setCity }) {
  const [query, setQuery] = useState(city.name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState(false);

  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const skipFetchRef = useRef(false);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Jab city bahar se badle (jaise Kudo ne chuni), input text bhi badal do
  useEffect(() => {
    if ((query || "").trim().toUpperCase() !== (city.name || "").toUpperCase()) {
      skipFetchRef.current = true;
      const nice = (city.name || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
      setQuery(nice);
    }
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city.name]);

  // Debounced fetch whenever the query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < CITY_SEARCH_MIN_CHARS) {
      setSuggestions([]);
      setLoading(false);
      setError(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(trimmed);
    }, CITY_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchSuggestions = async (term) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(false);
    try {
      
      const res = await fetch(
        `${CITY_SEARCH_API}${encodeURIComponent(term)}`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error("Network response was not ok");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setSuggestions(list.slice(0, 20));
      setOpen(true);
      setActiveIndex(-1);
    } catch (err) {
      if (err.name !== "AbortError") {
        // TODO: surface a proper error state/toast if needed
        setError(true);
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    const cityName = (item.CityName || "").toUpperCase();
    const countryName = (item.countryname || "").toUpperCase();
    const subtitle = countryName ? `CITY IN, ${countryName}` : "";
    setCity({ name: cityName, subtitle, country: countryName });
    setQuery(item.CityName || "");
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="hotel-search__field hotel-search__field--city" ref={wrapRef}>
      <span className="hotel-search__field-label">
        <MapPin size={14} /> City or Location
      </span>

      <input
        type="text"
        className="hotel-search__field-input"
        value={query}
        placeholder="Search city or country"
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {!open && (
        <span className="hotel-search__field-sub">{city.subtitle}</span>
      )}

      {open && (
        <div className="hotel-search__city-suggestions">
          {loading && (
            <div className="hotel-search__city-suggestion hotel-search__city-suggestion--status">
              Searching...
            </div>
          )}

          {!loading && error && (
            <div className="hotel-search__city-suggestion hotel-search__city-suggestion--status">
              Couldn't load suggestions. Try again.
            </div>
          )}

          {!loading &&
            !error &&
            suggestions.length === 0 &&
            query.trim().length >= CITY_SEARCH_MIN_CHARS && (
              <div className="hotel-search__city-suggestion hotel-search__city-suggestion--status">
                No matching cities found.
              </div>
            )}

          {!loading &&
            !error &&
            suggestions.map((item, index) => (
              <button
                type="button"
                key={`${item.CityName}-${item.countryname}-${index}`}
                className={
                  "hotel-search__city-suggestion" +
                  (index === activeIndex
                    ? " hotel-search__city-suggestion--active"
                    : "")
                }
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(item)}
              >
                <span className="hotel-search__city-suggestion-city">
                  {item.CityName}
                </span>
                <span className="hotel-search__city-suggestion-country">
                  {item.countryname}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }
  return cells;
}

function MonthTable({ year, month, minDate, checkIn, checkOut, hoverDate, onPick, onHover }) {
  const cells = buildMonthGrid(year, month);
  const min = startOfDay(minDate);

  return (
    <div className="hotel-search__cal-month">
      <div className="hotel-search__cal-month-title">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="hotel-search__cal-weekdays">
        {WEEKDAY_LABELS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="hotel-search__cal-grid">
        {cells.map((date, index) => {
          if (!date) {
            return <span key={`empty-${index}`} className="hotel-search__cal-cell hotel-search__cal-cell--empty" />;
          }

          const day = startOfDay(date);
          const disabled = day < min;
          const isCheckIn = isSameDay(day, checkIn);
          const isCheckOut = isSameDay(day, checkOut);

          const rangeEnd = checkOut || hoverDate;
          const inRange =
            checkIn &&
            rangeEnd &&
            day > startOfDay(checkIn) &&
            day < startOfDay(rangeEnd);

          const classNames = ["hotel-search__cal-cell"];
          if (disabled) classNames.push("hotel-search__cal-cell--disabled");
          if (isCheckIn || isCheckOut) classNames.push("hotel-search__cal-cell--selected");
          if (inRange) classNames.push("hotel-search__cal-cell--in-range");

          return (
            <button
              type="button"
              key={date.toISOString()}
              className={classNames.join(" ")}
              disabled={disabled}
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover && onHover(day)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangePicker({ checkIn, checkOut, setCheckIn, setCheckOut, minDate, onDone }) {
  const [viewDate, setViewDate] = useState(new Date(checkIn.getFullYear(), checkIn.getMonth(), 1));
  const [hoverDate, setHoverDate] = useState(null);

  const nextMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

  const goPrevMonth = () => {
    const prev = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    if (prev >= minMonth) setViewDate(prev);
  };

  const goNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handlePick = (day) => {
    // Starting fresh, or a full range already picked -> begin a new range
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(day);
      setCheckOut(null);
      return;
    }
    // checkIn is set, checkOut isn't -> this click completes (or restarts) the range
    if (day <= startOfDay(checkIn)) {
      setCheckIn(day);
      setCheckOut(null);
    } else {
      setCheckOut(day);
    }
  };

  return (
    <div className="hotel-search__cal-panel">
      <div className="hotel-search__cal-header">
        <button
          type="button"
          className="hotel-search__cal-nav"
          onClick={goPrevMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="hotel-search__cal-hint">
          {checkOut ? "Select check-in / check-out" : "Select check-out date"}
        </span>
        <button
          type="button"
          className="hotel-search__cal-nav"
          onClick={goNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="hotel-search__cal-months">
        <MonthTable
          year={viewDate.getFullYear()}
          month={viewDate.getMonth()}
          minDate={minDate}
          checkIn={checkIn}
          checkOut={checkOut}
          hoverDate={hoverDate}
          onPick={handlePick}
          onHover={setHoverDate}
        />
        <MonthTable
          year={nextMonthDate.getFullYear()}
          month={nextMonthDate.getMonth()}
          minDate={minDate}
          checkIn={checkIn}
          checkOut={checkOut}
          hoverDate={hoverDate}
          onPick={handlePick}
          onHover={setHoverDate}
        />
      </div>

      <div className="hotel-search__cal-actions">
        <button type="button" className="hotel-search__pax-done" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

// Search payload (form aur Kudo chatbot dono yahi use karte hain)
function buildHotelPayload(city, checkIn, checkOut, rooms) {
  const roomCount = rooms.length;
  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = rooms.reduce((sum, r) => sum + r.children, 0);
  const country = city.country || (city.subtitle || "").split(",").pop().trim();

  return {
    EngineId: HOTEL_ENGINE_ID,
    HotelDestination: `${city.name} , ${country}`,
    City: city.name,
    Country: country,
    CheckIn: toISODate(checkIn),
    CheckOut: checkOut ? toISODate(checkOut) : "",
    StartDate: toISODate(checkIn),
    EndDate: checkOut ? toISODate(checkOut) : "",
    countryres: DEFAULT_COUNTRY_RES,
    RoomCount: String(roomCount),
    Numofperson: `${totalAdults + totalChildren} `,
    Numofadults: totalAdults,
    NumofChild: totalChildren,
    Currency: DEFAULT_CURRENCY,
    Nationalitycode: DEFAULT_NATIONALITY_CODE,
    Night: checkOut ? String(nightsBetween(checkIn, checkOut)) : "1",
    room: rooms.map((r) => ({
      Adult: r.adults,
      child: Array.from({ length: r.children }, () => ({ Age: 0 })),
    })),
    HotelApiEngine: HOTEL_ENGINE_ID,
  };
}

// ====================== KUDO CHATBOT (HOTELS) ======================
const KD_BOT_NAME = "Kudo";

// Chat shuru hote hi pehle yahi sawaal poochha jata hai
const KD_LANG_PROMPT =
  "Hi! I'm Kudo, your hotel assistant.\n\nWould you like to chat in English or Hindi?\nType E for English.\nType H for Hindi.\n\n(Aap English me baat karenge ya Hindi me? English ke liye E likhein, Hindi ke liye H likhein.)";

// --- pure helpers start ---
const KD_MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const KD_MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const KD_MON = "(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*";

const kdHas = (t, words) => words.some((w) => t.includes(w));
const kdFmtDate = (d) => `${String(d.getDate()).padStart(2, "0")} ${KD_MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
const kdAddDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return startOfDay(x);
};
const kdDiffDays = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);

// Text me se saari dates nikalta hai (order me): "25 oct", "oct 25", "2026-10-25", "25/10", "tomorrow", "25-27 oct"
function kdParseDates(text) {
  const today = startOfDay(new Date());
  let s = " " + String(text).toLowerCase() + " ";
  const found = [];

  const push = (idx, d) => {
    if (d && !isNaN(d.getTime())) found.push({ idx, date: startOfDay(d) });
  };
  const blank = (re, fn) => {
    s = s.replace(re, (...a) => {
      const offset = a[a.length - 2];
      fn(a, offset);
      return " ".repeat(a[0].length);
    });
  };
  const mk = (monthIdx, day, year) => {
    const y = year ? Number(year) : today.getFullYear();
    let d = new Date(y, monthIdx, day);
    if (!year && d < today) d = new Date(y + 1, monthIdx, day);
    return d.getMonth() === monthIdx && d.getDate() === day ? d : null; // 31 feb jaisi galat date reject
  };

  // "25-27 oct", "25 to 27 oct 2026"
  blank(
    new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s*(?:-|to|se|till|until)\\s*(\\d{1,2})(?:st|nd|rd|th)?\\s+${KD_MON}(?:\\s+(\\d{4}))?\\b`, "g"),
    (a, o) => {
      const mi = KD_MONTHS.indexOf(a[3]);
      push(o, mk(mi, +a[1], a[4]));
      push(o + 1, mk(mi, +a[2], a[4]));
    }
  );
  // 2026-10-25
  blank(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g, (a, o) => {
    const d = new Date(+a[1], +a[2] - 1, +a[3]);
    push(o, d.getMonth() === +a[2] - 1 ? d : null);
  });
  // 25/10/2026, 25-10-26, 25/10
  blank(/\b(\d{1,2})[/\-.](\d{1,2})(?:[/\-.](\d{2,4}))?\b/g, (a, o) => {
    let y = a[3];
    if (y && y.length === 2) y = "20" + y;
    push(o, mk(+a[2] - 1, +a[1], y));
  });
  // 25 oct, 25th october 2026
  blank(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+${KD_MON}(?:\\s+(\\d{4}))?\\b`, "g"), (a, o) => {
    push(o, mk(KD_MONTHS.indexOf(a[2]), +a[1], a[3]));
  });
  // oct 25, october 25th 2026 ("may 2 adults" jaise phrases ko chhod ke)
  blank(
    new RegExp(`\\b${KD_MON}\\s+(\\d{1,2})(?:st|nd|rd|th)?(?!\\s*(?:adult|child|kid|room|night|guest|people|person|raat|din|bade|log))(?:,?\\s+(\\d{4}))?\\b`, "g"),
    (a, o) => push(o, mk(KD_MONTHS.indexOf(a[1]), +a[2], a[3]))
  );
  // keywords
  blank(/\b(day after tomorrow|parso|parson)\b/g, (a, o) => push(o, kdAddDays(today, 2)));
  blank(/\b(tomorrow|tmrw|tomorow|kal)\b/g, (a, o) => push(o, kdAddDays(today, 1)));
  blank(/\b(today|aaj)\b/g, (a, o) => push(o, today));

  return found.sort((x, y) => x.idx - y.idx).map((x) => x.date);
}

const kdParseNights = (t) => {
  const m = String(t).toLowerCase().match(/\b(\d{1,2})\s*(?:nights?|raat(?:e|en)?|din)\b/);
  return m ? Number(m[1]) : null;
};

const kdParseGuests = (text) => {
  const t = String(text).toLowerCase();
  const num = (re) => {
    const m = t.match(re);
    return m ? Number(m[1]) : null;
  };
  let adults = num(/\b(\d{1,2})\s*(?:adults?|bade|vayask|people|persons?|guests?|log|members?|pax)\b/);
  const children = num(/\b(\d{1,2})\s*(?:child(?:ren)?|kids?|bachch\w*|bacche|baccho)\b/);
  const rooms = num(/\b(\d{1,2})\s*(?:rooms?|kamr\w*|kamre)\b/);
  if (adults == null && /\b(couple|honeymoon)\b/.test(t)) adults = 2;
  if (adults == null && /\b(solo|akela|akele|alone)\b/.test(t)) adults = 1;
  return { adults, children, rooms };
};

const KD_FILLERS = [
  "mujhe", "mujhko", "muje", "hame", "humein", "i", "need", "want", "to", "book", "a", "an", "the", "find", "show",
  "me", "search", "dikhao", "dikha", "dhoondho", "dhundo", "dhoondh", "chahiye", "chahie", "hotel", "hotels", "in", "at",
  "near", "please", "pls", "bhai", "kindly", "looking", "for", "stay", "room", "rooms", "city", "mein", "mai", "ke", "liye",
  "kar", "do", "karo", "my", "is", "ka", "ki", "ko",
];
const kdStripFillers = (text) => {
  let w = String(text).toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).filter(Boolean);
  while (w.length && KD_FILLERS.includes(w[0])) w.shift();
  while (w.length && KD_FILLERS.includes(w[w.length - 1])) w.pop();
  return w.join(" ").trim();
};

// "hotels in goa for 2 adults" -> "goa", "goa me hotel chahiye" -> "goa"
const kdExtractCity = (text) => {
  const t = String(text).toLowerCase();
  const stop = "(?=\\s+(?:from|for|on|with|check|checkin|tomorrow|today|kal|aaj|parso|under|below|\\d)|[,.?!]|$)";
  let m = t.match(new RegExp(`(?:hotels?|stay|rooms?|resorts?|accommodation)\\s+(?:in|at|near|for)\\s+([a-z][a-z\\s]*?)${stop}`));
  if (!m) m = t.match(/([a-z][a-z\s]*?)\s+(?:me|mein|mai|ke)\s+(?:hotels?|stay|rooms?|resorts?)\b/);
  if (!m) m = t.match(new RegExp(`(?:stay|ruk(?:na|ne)?|thehar(?:na|ne)?)\\s+(?:in|at|near)\\s+([a-z][a-z\\s]*?)${stop}`));
  if (!m) return null;
  const c = kdStripFillers(m[1]);
  return c.length >= 2 ? c : null;
};

// City ke step par: "goa" ya "goa 25 oct 2 nights" -> "goa"
const kdCityFromBare = (text) => {
  let t = String(text).toLowerCase();
  t = t.split(/\d/)[0];
  t = t.split(/\b(?:from|for|on|with|check|checkin|tomorrow|today|kal|aaj|parso|adults?|rooms?|nights?)\b/)[0];
  const c = kdStripFillers(t);
  return c.length >= 2 ? c : null;
};

function kdBuildRooms(adults, children, roomCount) {
  const a = Math.max(1, adults || 1);
  const c = Math.max(0, children || 0);
  let n = Math.max(1, roomCount || 1);
  n = Math.min(n, a); // har room me kam se kam 1 adult
  n = Math.max(n, Math.ceil(a / 9), Math.ceil(c / 9));
  if (n > 5 || n > a) return null;
  const rooms = [];
  for (let i = 0; i < n; i++) {
    rooms.push({
      adults: Math.floor(a / n) + (i < a % n ? 1 : 0),
      children: Math.floor(c / n) + (i < c % n ? 1 : 0),
    });
  }
  return rooms;
}

const kdNewDraft = () => ({ city: null, checkIn: null, checkOut: null, nights: null, adults: null, children: null, rooms: null });

// User ke message se draft update karta hai. step = abhi kaunsa sawaal pending hai.
function kdAbsorb(draft, text, step, tr) {
  const t = String(text).toLowerCase();
  const out = { changed: false, errors: [], cityQuery: null };
  const today = startOfDay(new Date());

  // guests
  const g = kdParseGuests(t);
  if (g.adults != null) { draft.adults = g.adults; out.changed = true; }
  if (g.children != null) { draft.children = g.children; out.changed = true; }
  if (g.rooms != null) { draft.rooms = g.rooms; out.changed = true; }
  if (step === "guests" && g.adults == null && g.children == null && g.rooms == null) {
    const m = t.trim().match(/^(\d{1,2})$/);
    if (m) { draft.adults = Number(m[1]); out.changed = true; }
  }

  // dates
  const dates = kdParseDates(t);
  const nights = kdParseNights(t);
  if (dates.length >= 2) {
    draft.checkIn = dates[0];
    draft.checkOut = dates[1];
    out.changed = true;
  } else if (dates.length === 1) {
    const d = dates[0];
    if (step === "checkout" && draft.checkIn) {
      draft.checkOut = d;
    } else if (!draft.checkIn || step === "checkin") {
      draft.checkIn = d;
      if (draft.checkOut && draft.checkOut <= d) draft.checkOut = null;
    } else if (!draft.checkOut) {
      draft.checkOut = d;
    } else {
      const n = Math.max(1, kdDiffDays(draft.checkIn, draft.checkOut));
      draft.checkIn = d;
      draft.checkOut = kdAddDays(d, n);
    }
    out.changed = true;
  }
  if (nights) {
    if (draft.checkIn && dates.length < 2) draft.checkOut = kdAddDays(draft.checkIn, nights);
    else if (!draft.checkIn) draft.nights = nights;
    out.changed = true;
  }
  if (draft.checkIn && !draft.checkOut && draft.nights) draft.checkOut = kdAddDays(draft.checkIn, draft.nights);

  // validation
  if (draft.checkIn && draft.checkIn < today) {
    out.errors.push(tr("Check-in can't be a past date.", "Check-in date past me nahi ho sakti."));
    draft.checkIn = null;
    draft.checkOut = null;
  }
  if (draft.checkIn && draft.checkOut && draft.checkOut <= draft.checkIn) {
    out.errors.push(tr("Check-out must be after check-in.", "Check-out date, check-in ke baad honi chahiye."));
    draft.checkOut = null;
  }
  if (draft.adults != null && !kdBuildRooms(draft.adults, draft.children, draft.rooms)) {
    out.errors.push(
      tr(
        "That's too many guests. You can book up to 5 rooms, with at most 9 adults and 9 children per room.",
        "Itne guests allowed nahi hain. Maximum 5 rooms, aur har room me 9 adults aur 9 children tak."
      )
    );
    draft.adults = null;
    draft.children = null;
    draft.rooms = null;
  }

  // city
  if (step === "city" || step === "pickCity") out.cityQuery = kdExtractCity(t) || kdCityFromBare(text);
  else out.cityQuery = kdExtractCity(t);

  return out;
}

const kdCityObj = (c) => {
  const country = (c.country || "").toUpperCase();
  return { name: c.name.toUpperCase(), subtitle: country ? `CITY IN, ${country}` : "", country };
};

const kdYes = (t) =>
  /^(y|yes|yep|yeah|ya|yup|haan|han|ha|hanji|ji|ok|okay|confirm|search|go|done|theek hai|thik hai|kar do|kardo)\b/.test(String(t).trim().toLowerCase());
// --- pure helpers end ---

const kdFindCities = async (term) => {
  const res = await fetch(`${CITY_SEARCH_API}${encodeURIComponent(term)}`);
  if (!res.ok) throw new Error("City search failed");
  const data = await res.json();
  const list = Array.isArray(data) ? data : [];
  const seen = new Set();
  const out = [];
  for (const it of list) {
    const name = (it.CityName || "").trim();
    const country = (it.countryname || "").trim();
    if (!name) continue;
    const key = `${name.toLowerCase()}|${country.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name, country });
  }
  // exact naam wale pehle
  const q = term.toLowerCase();
  const exact = out.filter((c) => c.name.toLowerCase() === q);
  return (exact.length ? exact : out).slice(0, 5);
};

const kdAsk = (step, L) => {
  const tr = (en, hi) => (L === "E" ? en : hi);
  switch (step) {
    case "city":
      return tr("Which city or location would you like to stay in?", "Aap kis city ya location me hotel dhoondhna chahte hain?");
    case "checkin":
      return tr("What is your check-in date? (e.g. 25 Oct, 2026-10-25, tomorrow)", "Check-in date kaun si hai? (jaise 25 Oct, 2026-10-25, kal)");
    case "checkout":
      return tr('And the check-out date? (or say "2 nights")', 'Check-out date kaun si hai? (ya likho "2 raat")');
    case "guests":
      return tr('How many guests and rooms? (e.g. "2 adults 1 child 1 room")', 'Kitne log aur kitne room chahiye? (jaise "2 adults 1 child 1 room")');
    default:
      return "";
  }
};

const kdSummary = (d, L) => {
  const tr = (en, hi) => (L === "E" ? en : hi);
  const rooms = kdBuildRooms(d.adults, d.children, d.rooms) || [{ adults: 1, children: 0 }];
  const adults = rooms.reduce((s, r) => s + r.adults, 0);
  const kids = rooms.reduce((s, r) => s + r.children, 0);
  const n = Math.max(1, kdDiffDays(d.checkIn, d.checkOut));
  const where = d.city.country ? `${d.city.name}, ${d.city.country}` : d.city.name;
  return tr(
    `Please confirm your search:\n• City: ${where}\n• Check-in: ${kdFmtDate(d.checkIn)}\n• Check-out: ${kdFmtDate(d.checkOut)} (${n} night${n > 1 ? "s" : ""})\n• Rooms: ${rooms.length}, Adults: ${adults}, Children: ${kids}\n\nType YES to search hotels, or tell me what to change (e.g. "change dates", "3 adults").`,
    `Search confirm kar lijiye:\n• City: ${where}\n• Check-in: ${kdFmtDate(d.checkIn)}\n• Check-out: ${kdFmtDate(d.checkOut)} (${n} raat)\n• Rooms: ${rooms.length}, Adults: ${adults}, Children: ${kids}\n\nSearch karne ke liye YES likho, ya batao kya badalna hai (jaise "change dates", "3 adults").`
  );
};

// Hotel se jude common sawaal
const kdFaq = (t, L) => {
  const tr = (en, hi) => (L === "E" ? en : hi);

  if (kdHas(t, ["cancel", "refund", "paise wapas", "money back", "no show", "no-show"])) {
    return tr(
      "Cancellation and refund depend on the hotel's cancellation policy applicable at the time of booking. Charges, if any, are deducted as per that policy. A no-show or early departure may forfeit the booking amount. Please check the policy before you book.",
      "Cancellation aur refund booking ke time hotel ki cancellation policy ke hisaab se hota hai. Agar koi charge hai to usi policy ke anusaar kata jaata hai. No-show ya jaldi check-out par booking amount nahi mil sakta. Book karne se pehle policy zaroor dekh lein."
    );
  }
  if (kdHas(t, ["check-in time", "check in time", "checkin time", "check-out time", "check out time", "checkout time", "early check", "late check", "kitne baje", "timing"])) {
    return tr(
      "Standard check-in is 12:00 PM and check-out is 11:00 AM, unless the hotel specifies otherwise. Early check-in or late check-out depends on availability and may cost extra.",
      "Standard check-in 12:00 PM aur check-out 11:00 AM hota hai, jab tak hotel kuch aur na bataye. Early check-in ya late check-out availability par depend karta hai aur uska extra charge lag sakta hai."
    );
  }
  if (kdHas(t, ["document", "id proof", "passport", "aadhaar", "aadhar", "photo id", "pan card", "license", "kaunse id", "kaun si id"])) {
    return tr(
      "Carry a valid government-issued photo ID for every guest at check-in. The primary guest named on the booking voucher must be present at check-in.",
      "Check-in ke time har guest ke liye valid government photo ID saath rakhein. Booking voucher par jis guest ka naam hai, wo check-in par maujood hona chahiye."
    );
  }
  if (kdHas(t, ["child", "children", "kid", "bachch", "bacche", "extra bed", "occupancy", "maximum guest", "kitne log"])) {
    return tr(
      "The number of guests must not exceed the occupancy you booked. In the search you can add up to 5 rooms, with up to 9 adults and 9 children per room. Extra beds, meals or laundry that aren't part of the booking are paid directly at the hotel.",
      "Guests ki sankhya booking me li gayi occupancy se zyada nahi honi chahiye. Search me aap 5 rooms tak, aur har room me 9 adults aur 9 children tak add kar sakte hain. Booking me shamil na hone wale extra bed, meals ya laundry ka payment hotel me karna hota hai."
    );
  }
  if (kdHas(t, ["breakfast", "meal", "food", "khana", "naashta", "nashta", "laundry"])) {
    return tr(
      "Whether meals are included depends on the room and plan shown in the results. Anything not included in your booking is paid directly at the hotel.",
      "Meals included hain ya nahi, ye results me dikhne wale room aur plan par depend karta hai. Booking me shamil na hone wali cheezon ka payment hotel me karna hota hai."
    );
  }
  if (kdHas(t, ["payment", "pay", "upi", "card", "wallet", "gst"])) {
    return tr(
      "After you pick a hotel and room, you'll reach the payment page, where the available payment options are shown.",
      "Hotel aur room chunne ke baad aap payment page par jaoge, wahan available payment options dikhenge."
    );
  }
  if (kdHas(t, ["voucher", "ticket", "print", "confirmation"])) {
    return tr(
      "After booking you get a hotel booking voucher with your booking details. You can print it and show it with a photo ID at check-in.",
      "Booking ke baad aapko booking details wala hotel voucher milta hai. Use print karke photo ID ke saath check-in par dikha sakte hain."
    );
  }
  if (kdHas(t, ["offer", "discount", "deal", "coupon"])) {
    return tr(
      "You can see the latest deals in the \"Hotel Offers\" section on this page.",
      "Latest deals is page ke \"Hotel Offers\" section me dekh sakte hain."
    );
  }
  if (kdHas(t, ["support", "contact", "customer care", "helpline"])) {
    return tr(
      "We offer 24/7 support for booking-related queries. You can find the contact details in the footer, under Help & Support.",
      "Booking se jude sawaalon ke liye 24/7 support milta hai. Contact details footer me Help & Support ke neeche mil jayengi."
    );
  }
  if (kdHas(t, ["how to book", "kaise book", "how do i book", "kaise karu", "kaise kare"])) {
    return tr(
      'Just tell me the city, dates and guests (for example "hotels in Goa") and I\'ll search for you. You can also use the search bar on this page.',
      'Bas mujhe city, dates aur guests bata do (jaise "Goa me hotel") to main search kar dunga. Aap is page ka search bar bhi use kar sakte hain.'
    );
  }
  if (kdHas(t, ["thanks", "thank you", "shukriya", "dhanyawad", "thx"])) {
    return tr("Happy to help! Ask me anything else anytime.", "Khushi hui madad karke! Aur kuch chahiye to pooch lena.");
  }
  if (kdHas(t, ["help", "kya kar sakte", "what can you", "madad"])) {
    return tr(
      `Here's what I can do:\n• Search hotels for you (say "hotels in Goa")\n• Answer questions on check-in time, cancellation, documents, children and more\n• Type "cancel" anytime to stop a search`,
      `Main ye kar sakta hoon:\n• Aapke liye hotel search karna (bolo "Goa me hotel")\n• Check-in time, cancellation, documents, bachche jaise sawaalon ke jawab\n• Search rokni ho to kabhi bhi "cancel" likho`
    );
  }
  return null;
};

function KudoHotelChat({ onFillForm, onSearch, hidden }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [lang, setLangState] = useState(null); // null = abhi poochha nahi, "E" = English, "H" = Hindi
  const [step, setStepState] = useState(null); // booking flow ka current sawaal
  const [messages, setMessages] = useState([{ role: "bot", text: KD_LANG_PROMPT }]);

  const langRef = useRef(null);
  const stepRef = useRef(null);
  const draftRef = useRef(kdNewDraft());
  const cityOptionsRef = useRef([]);
  const bodyRef = useRef(null);

  const setLang = (v) => { langRef.current = v; setLangState(v); };
  const setStep = (v) => { stepRef.current = v; setStepState(v); };

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing, open, step]);

  const welcomeText = (L) =>
    L === "E"
      ? `Great, we'll chat in English! I'm Kudo. Tell me where you'd like to stay (for example "hotels in Goa") and I'll search for you. I can also answer questions on check-in time, cancellation, documents and more.\n\n(To switch language, type E or H.)`
      : `Badiya, hum Hindi me baat karenge! Main Kudo hoon. Bataiye aap kahan rukna chahte hain (jaise "Goa me hotel") to main aapke liye search kar dunga. Check-in time, cancellation, documents jaise sawaalon ke jawab bhi de sakta hoon.\n\n(Language badalne ke liye E ya H likho.)`;

  // Agla missing sawaal poochho, ya sab kuch mil gaya ho to summary dikhao
  const advance = (L) => {
    const d = draftRef.current;
    if (!d.city) { setStep("city"); return kdAsk("city", L); }
    if (!d.checkIn) { setStep("checkin"); return kdAsk("checkin", L); }
    if (!d.checkOut) { setStep("checkout"); return kdAsk("checkout", L); }
    if (d.adults == null) { setStep("guests"); return kdAsk("guests", L); }
    setStep("confirm");
    return kdSummary(d, L);
  };

  const reask = (L) => {
    const s = stepRef.current;
    return s === "confirm" ? kdSummary(draftRef.current, L) : s === "pickCity" ? pickCityText(L) : kdAsk(s, L);
  };

  const pickCityText = (L) => {
    const opts = cityOptionsRef.current;
    const tr = (en, hi) => (L === "E" ? en : hi);
    return tr(
      `I found a few matches. Reply with the number:\n${opts.map((c, i) => `${i + 1}. ${c.name}${c.country ? `, ${c.country}` : ""}`).join("\n")}`,
      `Kuch matches mile hain. Number likh kar batao:\n${opts.map((c, i) => `${i + 1}. ${c.name}${c.country ? `, ${c.country}` : ""}`).join("\n")}`
    );
  };

  const resolveCity = async (term, L) => {
    const tr = (en, hi) => (L === "E" ? en : hi);
    if (term.length < 3) {
      return { text: tr("Please type at least 3 letters of the city name.", "City ke naam ke kam se kam 3 akshar likho."), done: false };
    }
    try {
      let list = await kdFindCities(term);
      if (list.length === 0 && term.includes(" ")) {
        list = await kdFindCities(term.split(" ").pop()); // aakhri word se dobara try
      }
      if (list.length === 0) {
        return {
          text: tr(`I couldn't find "${term}". Please check the spelling or try another city.`, `"${term}" nahi mila. Spelling check karke ya dusri city likh kar try karo.`),
          done: false,
        };
      }
      if (list.length === 1) {
        draftRef.current.city = kdCityObj(list[0]);
        return { text: "", done: true };
      }
      cityOptionsRef.current = list;
      setStep("pickCity");
      return { text: pickCityText(L), done: false, picking: true };
    } catch (e) {
      console.error("Kudo city search failed:", e);
      return {
        text: tr("I couldn't reach the city search right now. Please try again in a moment.", "City search abhi nahi chal raha, thodi der baad dobara try karo."),
        done: false,
      };
    }
  };

  const doSearch = async (L) => {
    const tr = (en, hi) => (L === "E" ? en : hi);
    const d = draftRef.current;
    const rooms = kdBuildRooms(d.adults, d.children, d.rooms);
    const payload = buildHotelPayload(d.city, d.checkIn, d.checkOut, rooms);
    onFillForm({ city: d.city, checkIn: d.checkIn, checkOut: d.checkOut, rooms });
    const r = await onSearch(payload);
    if (r && r.ok) {
      const name = d.city.name;
      draftRef.current = kdNewDraft();
      setStep(null);
      return tr(
        `Searching hotels in ${name}... you'll be taken to the results page.`,
        `${name} me hotels dhoondh raha hoon... aapko results page par le jaaunga.`
      );
    }
    return tr(
      `Sorry, the search failed (${(r && r.error) || "unknown error"}). Type YES to try again, or "cancel" to stop.`,
      `Sorry, search fail ho gayi (${(r && r.error) || "unknown error"}). Dobara try karne ke liye YES likho, ya rokne ke liye "cancel".`
    );
  };

  // Booking flow ka ek turn. starting = true jab user ne abhi abhi hotel search maanga ho
  const bookingTurn = async (q, L, starting) => {
    const tr = (en, hi) => (L === "E" ? en : hi);
    const t = q.toLowerCase().trim();
    const d = draftRef.current;
    const step = stepRef.current;

    if (!starting) {
      if (/^(cancel|stop|restart|reset|exit|quit)\b/.test(t) || kdHas(t, ["start over", "new search", "naya search", "band karo"])) {
        draftRef.current = kdNewDraft();
        setStep(null);
        return tr(
          'Okay, I\'ve cancelled this search. Say "hotels in <city>" whenever you want to search again.',
          'Theek hai, search cancel kar di. Dobara dhoondhna ho to "<city> me hotel" likh dena.'
        );
      }

      if (step === "pickCity") {
        const n = parseInt(t, 10);
        const opts = cityOptionsRef.current;
        if (n >= 1 && n <= opts.length) {
          d.city = kdCityObj(opts[n - 1]);
          return advance(L);
        }
      }

      if (step === "confirm") {
        if (kdYes(t)) return doSearch(L);
        if (kdHas(t, ["change date", "date badal", "dates badal"])) { d.checkIn = null; d.checkOut = null; d.nights = null; return advance(L); }
        if (kdHas(t, ["change city", "city badal", "location badal"])) { d.city = null; return advance(L); }
        if (kdHas(t, ["change guest", "guests badal", "room badal", "change room"])) { d.adults = null; d.children = null; d.rooms = null; return advance(L); }
        if (/^(n|no|nahi|nope)\b/.test(t)) {
          return tr(
            'No problem. Tell me what to change (for example "change dates", "3 adults", or a new city), or type "cancel".',
            'Koi baat nahi. Batao kya badalna hai (jaise "change dates", "3 adults", ya nayi city), ya "cancel" likho.'
          );
        }
      }
    }

    const res = kdAbsorb(d, q, starting ? null : step, tr);
    let prefix = res.errors.length ? res.errors.join("\n") + "\n\n" : "";

    if (res.cityQuery) {
      const r = await resolveCity(res.cityQuery, L);
      if (r.picking) return prefix + r.text;
      if (!r.done) {
        if (step === "city" || starting || !d.city) { setStep("city"); return prefix + r.text; }
        prefix += r.text + "\n\n"; // pehle se city set hai, bas bata do
      }
    }

    if (!starting && !res.changed && !res.cityQuery) {
      const faq = kdFaq(t, L);
      if (faq) return `${faq}\n\n${reask(L)}`;
      return `${tr("Sorry, I didn't get that.", "Sorry, ye samajh nahi aaya.")}\n\n${reask(L)}`;
    }

    return prefix + advance(L);
  };

  const respond = async (q, L) => {
    const t = q.toLowerCase().trim();

    // Beech me language switch
    if (["e", "english", "eng"].includes(t)) { setLang("E"); return "Okay, switching to English. How can I help you?"; }
    if (["h", "hindi", "hin"].includes(t)) { setLang("H"); return "Theek hai, ab Hindi me baat karte hain. Bataiye, kya madad karoon?"; }

    // Search flow chal raha hai
    if (stepRef.current) return bookingTurn(q, L, false);

    // Nayi hotel search ka intent
    const tr = (en, hi) => (L === "E" ? en : hi);
    const hotelWord = kdHas(t, ["hotel", "stay", "resort", "room", "kamra", "kamre", "accommodation", "lodge", "ruk", "thehar"]);
    const searchVerb = kdHas(t, ["search", "find", "book", "looking", "need", "want", "show", "dhoond", "dhund", "dikha", "chahiye", "chahie", "suggest", "available", "plan"]);
    const city = kdExtractCity(t);
    const hasDates = kdParseDates(t).length > 0;

    if (hotelWord && (city || hasDates)) {
      draftRef.current = kdNewDraft();
      return bookingTurn(q, L, true);
    }

    const faq = kdFaq(t, L);
    if (faq) return faq;

    if (hotelWord && (searchVerb || t.split(/\s+/).length <= 3)) {
      draftRef.current = kdNewDraft();
      return bookingTurn(q, L, true);
    }

    if (/^(hi|hii|hello|hey|namaste|hlo|yo)\b/.test(t)) {
      return tr(
        `Hi! I'm ${KD_BOT_NAME}, your hotel assistant. Tell me where you'd like to stay (for example "hotels in Goa"), or ask me anything about hotel bookings.`,
        `Hi! Main ${KD_BOT_NAME} hoon, aapka hotel assistant. Bataiye aap kahan rukna chahte hain (jaise "Goa me hotel"), ya hotel booking se jude koi bhi sawaal poochiye.`
      );
    }

    return tr(
      `I didn't understand that. You can try:\n• "Hotels in Goa"\n• "Hotel check-in time?"\n• "Cancellation policy"\n• "What documents do I need?"`,
      `Ye mujhe samajh nahi aaya. Aap aisa try kar sakte ho:\n• "Goa me hotel"\n• "Hotel check-in time kya hai?"\n• "Cancellation policy"\n• "Kaunse documents chahiye?"`
    );
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || typing) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setTyping(true);
    await new Promise((r) => setTimeout(r, 350));

    let answer;
    try {
      // Pehle language poochhi jaati hai
      if (!langRef.current) {
        const c = q.toLowerCase();
        let chosen = null;
        if (["e", "english", "eng"].includes(c)) chosen = "E";
        else if (["h", "hindi", "hin"].includes(c)) chosen = "H";

        if (chosen) {
          setLang(chosen);
          answer = welcomeText(chosen);
        } else {
          answer = "Please type E for English or H for Hindi.\n(Kripya English ke liye E ya Hindi ke liye H likhein.)";
        }
      } else {
        answer = await respond(q, langRef.current);
      }
    } catch (e) {
      console.error("Kudo chatbot error:", e);
      answer = langRef.current === "E" ? "Something went wrong, please try again." : "Kuch gadbad ho gayi, dobara try karo.";
    }

    setMessages((m) => [...m, { role: "bot", text: answer }]);
    setTyping(false);
  };

  const quick =
    lang === "E"
      ? ["Find hotels in Goa", "Hotel check-in time?", "Cancellation policy", "What documents do I need?"]
      : ["Goa me hotel dhoondho", "Hotel check-in time kya hai?", "Cancellation policy", "Kaunse documents chahiye?"];

  const showLangChips = !lang;
  const showQuickChips = !!lang && !step && messages.length <= 3;
  const showConfirmChips = step === "confirm";
  const placeholder = !lang ? "Type E or H..." : lang === "E" ? "Ask me anything..." : "Kuch bhi poocho...";

  if (hidden) return null;

  return (
    <>
      <style>{`
        .kd-btn { position: fixed; right: 20px; bottom: 20px; z-index: 600; display: flex; align-items: center; gap: 10px;
          background: #fff; border: none; border-radius: 999px; padding: 8px 20px 8px 8px; cursor: pointer;
          box-shadow: 0 8px 24px rgba(6,26,51,.22); transition: transform .2s ease; font-family: inherit; }
        .kd-btn:hover { transform: translateY(-2px); }
        .kd-ico { width: 40px; height: 40px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #1a73e8, #0B2A5E); }
        .kd-btn-text { font-size: 14px; font-weight: 700; color: #0B2A5E; }
        .kd-box { position: fixed; right: 20px; bottom: 20px; z-index: 700; width: 370px; max-width: calc(100vw - 24px);
          height: 540px; max-height: calc(100vh - 100px); background: #fff; border-radius: 18px; display: flex; flex-direction: column;
          box-shadow: 0 20px 50px rgba(6,26,51,.32); overflow: hidden; font-family: 'Manrope','Segoe UI',system-ui,sans-serif; color: #0F1B33; }
        .kd-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px;
          background: linear-gradient(120deg, #0B2A5E, #1a73e8); color: #fff; }
        .kd-title { font-size: 17px; font-weight: 800; display: flex; align-items: center; gap: 8px; }
        .kd-sub { font-size: 11.5px; opacity: .8; font-weight: 500; }
        .kd-x { width: 32px; height: 32px; border-radius: 50%; border: none; background: rgba(255,255,255,.18); color: #fff; cursor: pointer; font-size: 14px; }
        .kd-body { flex: 1; overflow-y: auto; padding: 14px; background: #F6F8FC; display: flex; flex-direction: column; gap: 10px; }
        .kd-msg { max-width: 86%; padding: 10px 13px; border-radius: 14px; font-size: 13.5px; line-height: 1.45; white-space: pre-line; word-break: break-word; }
        .kd-user { align-self: flex-end; background: #0B2A5E; color: #fff; border-bottom-right-radius: 4px; }
        .kd-bot { align-self: flex-start; background: #fff; border: 1px solid #E8EAF0; border-bottom-left-radius: 4px; }
        .kd-typing { color: #6B7385; font-style: italic; }
        .kd-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .kd-chip { border: 1px solid #C9D5EC; background: #fff; color: #1a73e8; border-radius: 999px; padding: 7px 12px;
          font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
        .kd-chip:hover { background: #EEF5FF; }
        .kd-chip-lang { padding: 9px 18px; font-size: 13.5px; font-weight: 700; }
        .kd-bar { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid #E8EAF0; background: #fff; }
        .kd-input { flex: 1; min-width: 0; border: none; outline: none; font-size: 16px; font-family: inherit; color: #0F1B33; background: transparent; }
        .kd-send { width: 38px; height: 38px; border-radius: 50%; border: none; background: #1a73e8; cursor: pointer;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .kd-send:disabled { opacity: .5; cursor: not-allowed; }
        @media (max-width: 560px) {
          .kd-btn-text { display: none; }
          .kd-btn { padding: 8px; right: 14px; }
          .kd-box { right: 0; left: 0; bottom: 0; width: 100%; max-width: 100%; max-height: 92vh; height: 92vh; border-radius: 18px 18px 0 0; }
        }
      `}</style>

      {!open && (
        <button className="kd-btn" onClick={() => setOpen(true)} aria-label={`Open ${KD_BOT_NAME} chat`}>
          <span className="kd-ico">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="5" width="18" height="14" rx="4" />
              <circle cx="9" cy="12" r="1" />
              <circle cx="15" cy="12" r="1" />
            </svg>
          </span>
          <span className="kd-btn-text">Ask {KD_BOT_NAME}</span>
        </button>
      )}

      {open && (
        <div className="kd-box" role="dialog" aria-label={`${KD_BOT_NAME} chat`}>
          <div className="kd-head">
            <div>
              <div className="kd-title">{KD_BOT_NAME}</div>
              <div className="kd-sub">Hotel assistant</div>
            </div>
            <button className="kd-x" onClick={() => setOpen(false)} aria-label="Close">✕</button>
          </div>

          <div className="kd-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`kd-msg ${m.role === "user" ? "kd-user" : "kd-bot"}`}>{m.text}</div>
            ))}

            {showLangChips && (
              <div className="kd-chips">
                <button className="kd-chip kd-chip-lang" onClick={() => send("E")}>E · English</button>
                <button className="kd-chip kd-chip-lang" onClick={() => send("H")}>H · Hindi</button>
              </div>
            )}

            {showQuickChips && (
              <div className="kd-chips">
                {quick.map((q) => (
                  <button key={q} className="kd-chip" onClick={() => send(q)}>{q}</button>
                ))}
              </div>
            )}

            {showConfirmChips && (
              <div className="kd-chips">
                <button className="kd-chip" onClick={() => send("YES")}>{lang === "E" ? "Yes, search" : "Haan, search karo"}</button>
                <button className="kd-chip" onClick={() => send("change dates")}>{lang === "E" ? "Change dates" : "Dates badlo"}</button>
                <button className="kd-chip" onClick={() => send("cancel")}>{lang === "E" ? "Cancel" : "Cancel"}</button>
              </div>
            )}

            {typing && <div className="kd-msg kd-bot kd-typing">Typing...</div>}
          </div>

          <div className="kd-bar">
            <input
              className="kd-input"
              placeholder={placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
            <button className="kd-send" onClick={() => send()} disabled={typing || !input.trim()} aria-label="Send">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <path d="M12 19V5M12 5l-6 6M12 5l6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function HotelSearchEngine({ onSearch }) {
  const today = new Date();
  const defaultCheckIn = new Date(today);
  defaultCheckIn.setDate(today.getDate() + 2);
  const defaultCheckOut = new Date(today);
  defaultCheckOut.setDate(today.getDate() + 3);

  const [selectedStars, setSelectedStars] = useState([]);
  const [checkIn, setCheckIn] = useState(defaultCheckIn);
  const [checkOut, setCheckOut] = useState(defaultCheckOut);
  const [rooms, setRooms] = useState([{ adults: 1, children: 0 }]);
  const [paxOpen, setPaxOpen] = useState(false);
  const paxRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [city, setCity] = useState(DEMO_CITY);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dateRef = useRef(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Auto-rotate hero background images; search bar / content stay fixed on top
  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (paxRef.current && !paxRef.current.contains(event.target)) {
        setPaxOpen(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setDatePickerOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-close the calendar once a full check-in/check-out range is chosen
  useEffect(() => {
    if (checkIn && checkOut) setDatePickerOpen(false);
  }, [checkIn, checkOut]);

  const handleSetCheckIn = (day) => {
    setCheckIn(day);
    if (checkOut && day >= startOfDay(checkOut)) {
      setCheckOut(null);
    }
  };

  const toggleStar = (star) => {
    setSelectedStars((prev) =>
      prev.includes(star) ? prev.filter((s) => s !== star) : [...prev, star]
    );
  };

  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const checkInParts = formatDateParts(checkIn);
  const checkOutParts = checkOut
    ? formatDateParts(checkOut)
    : { day: "--", month: "", year: "", weekday: "Select date" };

  const buildPayload = () => buildHotelPayload(city, checkIn, checkOut, rooms);

  // Form ka search aur Kudo chatbot ka search, dono isi se chalte hain
  const runSearch = async (hotelpayload) => {
    console.log("Hotel search payload:", hotelpayload);

    if (onSearch) {
      onSearch(hotelpayload);
      return { ok: true };
    }

    setSearching(true);
    setSearchError(null);

    try {
      const res = await fetch(HOTEL_SEARCH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(hotelpayload),
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const data = await res.json();
      // This is the response the search should print to console.
      console.log("Hotel search response:", data);

      try {
        sessionStorage.setItem("hotelSearchPayload", JSON.stringify(hotelpayload));
        sessionStorage.setItem("hotelSearchResponse", JSON.stringify(data));
      } catch (err) {
        console.error("Could not persist hotel search result", err);
      }

      window.location.href = "/hotel-results";
      return { ok: true };
    } catch (err) {
      console.error("Hotel search request failed:", err);
      const message = err.message || "Something went wrong while searching hotels.";
      setSearchError(message);
      return { ok: false, error: message };
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = () => runSearch(buildPayload());

  // Kudo ne jo city/dates/guests chune, wo search form me bhi dikha do
  const fillFromBot = ({ city: c, checkIn: ci, checkOut: co, rooms: rs }) => {
    setCity(c);
    setCheckIn(ci);
    setCheckOut(co);
    setRooms(rs);
  };

  return (
    <section className="hotel-search">
      {searching && (
        <div className="car-loader-overlay">
          <CarLoader
            isLoading={searching}
            label={`Searching hotels in ${city.name}...`}
          />
        </div>
      )}

      <div className="hotel-search__hero">
        <div className="hotel-search__hero-slider" aria-hidden="true">
          {HERO_SLIDES.map((src, index) => (
            <div
              key={src}
              className={
                "hotel-search__hero-slide" +
                (index === slideIndex ? " hotel-search__hero-slide--active" : "")
              }
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
          <div className="hotel-search__hero-overlay" />
        </div>

        <div className="hotel-search__container hotel-search__hero-content">
          <h2 className="hotel-search__hero-title">
            Domestic &amp; International Hotels
          </h2>

          <div className="hotel-search__star-row">
            <span className="hotel-search__star-label">Preferred star rating</span>
            <div className="hotel-search__star-options">
              {STAR_OPTIONS.map((star) => (
                <label className="hotel-search__star-option" key={star}>
                  <input
                    type="checkbox"
                    checked={selectedStars.includes(star)}
                    onChange={() => toggleStar(star)}
                  />
                  <span>
                    {star} <Star size={12} />
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="hotel-search__engine hotel-search__engine--sticky">
            <CityAutosuggest city={city} setCity={setCity} />

            <div className="hotel-search__date-group" ref={dateRef}>
              <button
                type="button"
                className="hotel-search__field hotel-search__field--date hotel-search__field--date-btn"
                onClick={() => setDatePickerOpen((open) => !open)}
              >
                <span className="hotel-search__field-label">
                  <Calendar size={14} /> Check-In
                </span>
                <span className="hotel-search__field-value">
                  {checkInParts.day} {checkInParts.month} {checkInParts.year}
                </span>
                <span className="hotel-search__field-sub">{checkInParts.weekday}</span>
              </button>

              <button
                type="button"
                className="hotel-search__field hotel-search__field--date hotel-search__field--date-btn"
                onClick={() => setDatePickerOpen((open) => !open)}
              >
                <span className="hotel-search__field-label">
                  <Calendar size={14} /> Check-Out
                </span>
                <span className="hotel-search__field-value">
                  {checkOutParts.day} {checkOutParts.month} {checkOutParts.year}
                </span>
                <span className="hotel-search__field-sub">{checkOutParts.weekday}</span>
              </button>

              {datePickerOpen && (
                <DateRangePicker
                  checkIn={checkIn}
                  checkOut={checkOut}
                  setCheckIn={handleSetCheckIn}
                  setCheckOut={setCheckOut}
                  minDate={today}
                  onDone={() => setDatePickerOpen(false)}
                />
              )}
            </div>

            <div className="hotel-search__field hotel-search__field--pax" ref={paxRef}>
              <span className="hotel-search__field-label">
                <Users size={14} /> Rooms &amp; Guests
              </span>
              <button
                type="button"
                className="hotel-search__field-value hotel-search__pax-trigger"
                onClick={() => setPaxOpen((open) => !open)}
              >
                {rooms.length} Room{rooms.length > 1 ? "s" : ""} &nbsp;
                {totalAdults} Adult{totalAdults > 1 ? "s" : ""}
              </button>

              {paxOpen && (
                <RoomsGuestsPanel
                  rooms={rooms}
                  setRooms={setRooms}
                  onDone={() => setPaxOpen(false)}
                />
              )}
            </div>

            <div className="hotel-search__field hotel-search__field--action">
              <button
                type="button"
                className="hotel-search__submit"
                onClick={handleSearch}
                disabled={searching}
              >
                <Search size={16} />
                {searching ? "SEARCHING..." : "SEARCH"}
              </button>
            </div>
          </div>

          {searchError && (
            <div className="hotel-search__error">{searchError}</div>
          )}
        </div>
      </div>

      <div className="hotel-search__container hotel-search__offers">
        <h3 className="hotel-search__section-title">
          <b>Hotel</b> Offers
        </h3>
        <div className="hotel-search__offers-grid">
          {DEMO_OFFERS.map((offer) => (
            <div className="hotel-search__offer-card" key={offer.id}>
              <img src={offer.image} alt={offer.alt} />
            </div>
          ))}
        </div>
      </div>

      <div className="hotel-search__why">
        <div className="hotel-search__container">
          <h3 className="hotel-search__section-title hotel-search__section-title--center">
            <b>Why Booking with Us</b>
          </h3>
          <div className="hotel-search__why-grid">
            {WHY_BOOK_WITH_US.map(({ id, icon: Icon, title, text }) => (
              <div className="hotel-search__why-card" key={id}>
                <div className="hotel-search__why-icon">
                  <Icon size={28} />
                </div>
                <h4>{title}</h4>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hotel-search__app">
        <div className="hotel-search__container hotel-search__app-inner">
          <div className="hotel-search__app-copy">
            <h5>Get more out of our app with the mobile experience</h5>
            <p>
              Download the mobile app for one-touch access to your next travel
              adventure. You'll get access to hidden features and special offers.
            </p>
            <ul className="hotel-search__app-features">
              <li>Get exclusive offers &amp; prices</li>
              <li>Manage trip and ticket booking</li>
              <li>Activity suggestions based on destination</li>
              <li>All trip bookings in a single platform</li>
            </ul>
            <div className="hotel-search__app-links">
              <span className="hotel-search__app-link">
                <Smartphone size={16} /> Google Play
              </span>
              <span className="hotel-search__app-link">
                <Smartphone size={16} /> App Store
              </span>
            </div>
          </div>
          <div className="hotel-search__app-qr">
            <h2>QR Code</h2>
            <span>Scan to download</span>
          </div>
        </div>
      </div>

      <KudoHotelChat onFillForm={fillFromBot} onSearch={runSearch} hidden={searching} />
    </section>
  );
} 